import numeral from 'numeral'
import { useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { Card } from 'src/components/atoms/Card'
import { ButtonLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import {
  Heading3,
  Heading5,
  ParagraphNoSpace
} from 'src/components/atoms/Typography'
import { HistoryGraph } from 'src/components/molecules/HistoryGraph'
import { Pagination } from 'src/components/molecules/Pagination'
import { ZeroState } from 'src/components/molecules/ZeroState'
import { FlowStepper } from 'src/components/organisms/FlowStepper'
import { DashboardApplicationsTable } from 'src/components/organisms/tables/DashboardApplicationsTable'

import {
  useGetAccountAndPlanterRegistrationQuery,
  useGetDashboardDataQuery,
  useGetDashboardTabsDataQuery
} from 'src/config/generated/graphql'
import {
  listApplicationsPath,
  listProjectsPath,
  viewAccountPlanterRegistrationPath,
  viewApplicationPath,
  viewProjectDetailPath
} from 'src/config/paths'
import { useHasPlanterExperience } from 'src/hooks/useHasPlanterExperience'
import { usePagination } from 'src/hooks/usePagination'
import { ApplicationWorkflowState } from 'src/types/application'

import { buildPlanterGraphData } from '../../helper'
import { GettingStartedGuides } from './GettingStartedGuides'
import { PlanterActionPanel } from './PlanterActionPanel'
import { getSteps, gettingStartedLinks } from './helper'

const PlanterDashboardPanel = () => {
  const { user } = useUser()
  const { hasPlanterExperience, planterExperience } = useHasPlanterExperience()
  const { limit, currentPage, offset, onPageClick } = usePagination({
    limit: 5
  })

  const { data: dashboardTabsData, loading: dashboardTabsDataLoading } =
    useGetDashboardTabsDataQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        accountId: user?.Account?.Id,
        limit,
        offset
      }
    })

  const { data: dashboardData } = useGetDashboardDataQuery()

  const { data: accountData } = useGetAccountAndPlanterRegistrationQuery({
    variables: {
      id: user?.ActiveAccountId
    }
  })

  const totalCount = useMemo(() => {
    return dashboardTabsData?.Application_aggregate?.aggregate?.count || 0
  }, [dashboardTabsData?.Application_aggregate])

  const projectCount = useMemo(
    () => accountData?.Account_by_pk?.Planter?.Projects.length || 0,
    [accountData]
  )

  const defaultProject = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.length === 1
        ? accountData.Account_by_pk.Planter.Projects[0]
        : null,
    [accountData]
  )

  const hasPastProjects = useMemo(() => {
    return (
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(a =>
          // Number(a.Season?.Name) <= dayjs().year() &&
          [
            ApplicationWorkflowState.Approved,
            ApplicationWorkflowState.Planted,
            ApplicationWorkflowState.Monitoring,
            ApplicationWorkflowState.Declined,
            ApplicationWorkflowState.Withdrawn
          ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ) || false
    )
  }, [accountData])

  const hasApprovedRequest = useMemo(() => {
    return (
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(a =>
          [
            ApplicationWorkflowState.Approved,
            ApplicationWorkflowState.Planted,
            ApplicationWorkflowState.Monitoring
          ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ) || false
    )
  }, [accountData])

  const hasPendingRequest = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(
          a => a.WorkflowState === ApplicationWorkflowState.Pending
        )
      ),
    [accountData]
  )

  const canRequestTrees = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(
          a =>
            ![
              ApplicationWorkflowState.Pending,
              ApplicationWorkflowState.Declined,
              ApplicationWorkflowState.Approved
            ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ),
    [accountData]
  )

  const hasValidPlanterExperience = useMemo(
    () => hasPlanterExperience && planterExperience?.WorkflowState !== 'Draft',
    [hasPlanterExperience, planterExperience]
  )

  const steps = useMemo(
    () =>
      getSteps({
        defaultProject,
        hasApprovedRequest,
        hasPastProjects,
        hasValidPlanterExperience,
        projectCount
      }),
    [
      defaultProject,
      hasApprovedRequest,
      hasPastProjects,
      hasValidPlanterExperience,
      projectCount
    ]
  )

  // the order of the if statements in this function is important - don't change
  const panelContent = useMemo(() => {
    if (hasApprovedRequest) {
      return (
        <>
          <Card className='flex-1'>
            <div className='text-mostlyGreen font-bold flex flex-col gap-[10px]'>
              <span className='text-[30px] md:text-[40px] leading-none'>
                {numeral(
                  dashboardData?.PlanterStatsForUser[0]?.TreesApproved || 0
                ).format('0,0')}
              </span>
              <span className='text-[16px] md:text-[20px] leading-none'>
                Native trees approved
              </span>
            </div>
            <HistoryGraph data={buildPlanterGraphData(dashboardData)} />
          </Card>
          <Card
            size='large'
            className='w-full lg:w-[380px] flex flex-col gap-[10px]'
          >
            <Heading3 className='!m-0 text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
              Request native trees
            </Heading3>
            <PlanterActionPanel />
          </Card>
        </>
      )
    }
    if (hasPendingRequest) {
      const defaultApplication = defaultProject?.Applications.find(
        a => a.WorkflowState === ApplicationWorkflowState.Pending
      )
      return (
        <>
          <Card className='flex-1 flex flex-col gap-5'>
            <Heading3 className='!m-0 !text-[24px] !leading-[30px] !font-bold md:!text-[28px] md:!leading-[39px] md:!font-semibold'>
              We have your request
            </Heading3>
            <ParagraphNoSpace className='font-medium !text-[14px] md:!text-[16px] !leading-[20px] md:!leading-[24px]'>
              Log back in here to see the status of your request. You’ll hear
              from us when we start assessing all requests.
            </ParagraphNoSpace>
            <Heading5>You can view your information anytime</Heading5>
            <ul className='list-normal'>
              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={viewAccountPlanterRegistrationPath}
                >
                  Planting experience
                </ButtonLink>
              </li>
              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={
                    defaultProject
                      ? viewProjectDetailPath({
                          routeParams: {
                            projectId: defaultProject?.Id
                          }
                        })
                      : listProjectsPath
                  }
                >
                  Planting project details
                </ButtonLink>
              </li>

              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={
                    defaultProject && defaultApplication
                      ? viewApplicationPath({
                          routeParams: {
                            projectId: defaultProject?.Id,
                            applicationId: defaultApplication.Id
                          }
                        })
                      : listApplicationsPath
                  }
                >
                  Tree request{' '}
                  {defaultProject && defaultApplication
                    ? `(ref: ${defaultApplication?.Reference})`
                    : ''}
                </ButtonLink>
              </li>
            </ul>
          </Card>
          <GettingStartedGuides links={gettingStartedLinks} />
        </>
      )
    }
    if (canRequestTrees) {
      return (
        <>
          <Card className='flex-1'>
            <ZeroState
              title='Start your planting journey!'
              description='Request, receive and manage your native trees.'
              image='/img/zero-states/planter.png'
            />
          </Card>
          <Card
            size='large'
            className='w-full lg:w-[380px] flex flex-col gap-[10px]'
          >
            <Heading3 className='!m-0 text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
              Request native trees
            </Heading3>
            <PlanterActionPanel />
          </Card>
        </>
      )
    }

    return (
      <>
        <Card className='flex-1 flex flex-col gap-5'>
          <Heading3 className='!m-0 !text-[24px] !leading-[30px] !font-bold md:!text-[28px] md:!leading-[39px] md:!font-semibold'>
            Apply for trees for next season
          </Heading3>
          <ParagraphNoSpace className='font-medium !text-[14px] md:!text-[16px] !leading-[20px] md:!leading-[24px]'>
            If your application was declined, you can always try again for the
            next season.
          </ParagraphNoSpace>
          <Heading5>View information you’ve provided previously</Heading5>
          <ul className='list-normal'>
            <li>
              <ButtonLink
                variant='text'
                className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                href={viewAccountPlanterRegistrationPath}
              >
                Planting experience
              </ButtonLink>
            </li>
            <li>
              <ButtonLink
                variant='text'
                className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                href={
                  defaultProject
                    ? viewProjectDetailPath({
                        routeParams: {
                          projectId: defaultProject?.Id
                        }
                      })
                    : listProjectsPath
                }
              >
                Planting project
              </ButtonLink>
            </li>
          </ul>
        </Card>
        <Card
          size='large'
          className='w-full lg:w-[380px] flex flex-col gap-[10px]'
        >
          <Heading3 className='!m-0 text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
            Request native trees
          </Heading3>
          <PlanterActionPanel />
        </Card>
      </>
    )
  }, [
    canRequestTrees,
    dashboardData,
    defaultProject,
    hasApprovedRequest,
    hasPendingRequest
  ])

  const hasApplications = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.reduce(
        (acc, curr) => acc + curr.Applications.length || 0,
        0
      ),
    [accountData]
  )

  return (
    <LoadingSpinner isLoading={dashboardTabsDataLoading}>
      {!hasApplications ? (
        <div className='flex flex-col gap-5 lg:gap-10'>
          <Card className='flex flex-col gap-10'>
            <FlowStepper steps={steps} />
          </Card>
          <div className='flex flex-col lg:flex-row gap-5 lg:gap-10'>
            <Card size='large' className='flex flex-col flex-1'>
              <ZeroState
                title='Start your planting journey!'
                description='Request, receive and manage your native trees.'
                image='/img/zero-states/planter.png'
              />
            </Card>
            <GettingStartedGuides narrow links={gettingStartedLinks} />
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-5 md:gap-10'>
          <Card>
            <Heading3 className='!m-0 !text-[24px] !leading-[30px] !font-bold md:!text-[28px] md:!leading-[39px] md:!font-semibold'>
              Manage your trees
            </Heading3>
            <DashboardApplicationsTable
              applicationsData={dashboardTabsData?.Application || []}
            />
            {totalCount > limit ? (
              <Pagination
                page={currentPage}
                pageSize={limit}
                onChange={onPageClick}
                count={totalCount}
              />
            ) : null}
          </Card>
          <div className='flex flex-col lg:flex-row gap-5 md:gap-10'>
            {panelContent}
          </div>
        </div>
      )}
    </LoadingSpinner>
  )
}

export default PlanterDashboardPanel
